/**
 * Invite Partner Dialog - uses shared UI components.
 *
 * Business language throughout - no engineering jargon.
 */

import { useState, useMemo, useEffect, useRef } from 'react'
import {
  ROW_SELECTED,
  ROW_UNSELECTED,
} from '@/admin/pages/partnerships/selection-styles'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'
import { z } from 'zod'
import { useQueryClient } from '@tanstack/react-query'
import api from '@/admin/lib/api'
import { useOrg } from '@/admin/hooks/use-org'
import { usePartnerships } from '@/admin/hooks/use-partnerships'
import { useExternalRoles } from '@/admin/hooks/use-external-roles'
import { usePartnershipProductRoster } from '@/admin/hooks/use-partnership-product-access'
import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import { Input } from '@/shared/components/ui/input'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/shared/components/ui/dialog'
import {
  ProductAccessPicker,
  type ProductAccessPickerProduct,
} from '@/admin/components/shared/product-access-picker'
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

import { toastApiError } from '@/shared/lib/api-error'
function ParentPartnerPicker({
  selected,
  onChange,
  initialTerm,
  onSearch,
  results,
}: {
  selected: string
  onChange: (uuid: string) => void
  initialTerm: string
  onSearch: (term: string) => void
  results: { uuid: string; downstream_organisation: { name: string } }[]
}) {
  const { t } = useTranslation()
  const [term, setTerm] = useState(initialTerm)
  const chosen = results.find((p) => p.uuid === selected)

  return (
    <div className="space-y-2">
      <Label htmlFor="partners-parent-input">
        {t('partnerships.invitePartnerParentLabel', 'Parent partner')}
      </Label>
      <Input
        id="partners-parent-input"
        data-testid="partners-parent-input"
        value={term}
        onChange={(e) => {
          setTerm(e.target.value)
          onSearch(e.target.value)
        }}
        placeholder={t(
          'partnerships.invitePartnerParentSearchPlaceholder',
          'Search partners by name',
        )}
      />
      {chosen && (
        <p className="text-[12px] text-[var(--ink-2)]">
          {t('partnerships.invitePartnerParentSelected', 'Selected: {{name}}', {
            name: chosen.downstream_organisation.name,
          })}
        </p>
      )}
      <div className="max-h-[150px] overflow-y-auto rounded-[6px] border-[0.5px] border-[var(--rule)]">
        {term.length < 2 ? (
          <p className="px-3 py-3 text-[12px] text-[var(--ink-3)]">
            {t(
              'partnerships.invitePartnerParentHint',
              'Type at least two characters to find a partner.',
            )}
          </p>
        ) : results.length === 0 ? (
          <p className="px-3 py-3 text-[12px] text-[var(--ink-3)]">
            {t('partnerships.invitePartnerParentNoMatches', 'No partners match that.')}
          </p>
        ) : (
          results.map((p) => (
            <Button
              key={p.uuid}
              variant="ghost"
              data-testid={`partners-parent-option-${p.uuid}`}
              onClick={() => onChange(p.uuid)}
              className={`h-auto w-full justify-start rounded-none px-3 py-2 text-[13px] font-normal ${
                selected === p.uuid ? ROW_SELECTED : ROW_UNSELECTED
              }`}
            >
              {p.downstream_organisation.name}
            </Button>
          ))
        )}
      </div>
    </div>
  )
}


interface InvitePartnerDialogProps {
  open: boolean
  onClose?: () => void
  onOpenChange?: (open: boolean) => void
  onCreated?: () => void
  onSuccess?: () => void
}

const STRATEGY_OPTIONS = [
  {
    value: 'managed',
    label: 'We manage their users',
    description: 'You control who at the partner has access',
  },
  {
    value: 'delegated',
    label: 'They manage their own users',
    description: 'The partner adds and removes their own people',
  },
  {
    value: 'direct_inheritance',
    label: 'All users get access automatically',
    description: 'Everyone at the partner organisation gets this role',
  },
]

// ---------------------------------------------------------------------------
// Product selector (required) - a bespoke composite widget embedded in the
// generated form via `type: 'custom'`. Holds its own search state so keystrokes
// don't churn the parent form's field memo. Selection lives in form state; the
// `.min(1)` schema surfaces an error (highlighting this field) when Send is
// clicked with no product chosen. Per the CLAUDE.md required-field rule the Send
// button is NEVER disabled for this - it stays enabled and the click highlights
// the missing field.
// ---------------------------------------------------------------------------

interface InviteProductSelectorProps {
  products: ProductAccessPickerProduct[]
  loading: boolean
  selected: string[]
  onChange: (next: string[]) => void
  label: string
  hint: string
  emptyMessage: string
  error?: string
}

function InviteProductSelector({
  products,
  loading,
  selected,
  onChange,
  label,
  hint,
  emptyMessage,
  error,
}: InviteProductSelectorProps) {
  const [search, setSearch] = useState('')
  const selectedSet = useMemo(() => new Set(selected), [selected])

  const toggle = (uuid: string) => {
    const next = new Set(selectedSet)
    if (next.has(uuid)) {
      next.delete(uuid)
    } else {
      next.add(uuid)
    }
    onChange(Array.from(next))
  }

  return (
    <div className="col-span-2 flex flex-col gap-1.5" data-testid="invite-products-field">
      <span
        className={`text-[11px] uppercase tracking-[0.1em] ${
          error ? 'text-[var(--status-error)]' : 'text-[var(--ink-2)]'
        }`}
      >
        {label} *
      </span>
      <span className="text-[11px] text-[var(--ink-3)]">{hint}</span>
      {loading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="h-4 w-4 animate-spin text-[var(--ink-3)]" />
        </div>
      ) : (
        <div
          className={
            error
              ? 'rounded-[6px] ring-1 ring-[var(--status-error)] ring-offset-2 ring-offset-[var(--bg)]'
              : undefined
          }
        >
          <ProductAccessPicker
            availableProducts={products}
            selected={selectedSet}
            onToggle={toggle}
            onSelectAll={() => onChange(products.map((p) => p.uuid))}
            onClearAll={() => onChange([])}
            search={search}
            onSearch={setSearch}
            emptyMessage={emptyMessage}
          />
        </div>
      )}
      {error && (
        <span className="text-[11px] text-[var(--status-error)]">{error}</span>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function InvitePartnerDialog({
  open,
  onClose,
  onOpenChange,
  onCreated,
  onSuccess,
}: InvitePartnerDialogProps) {
  const { t } = useTranslation()
  const { orgUuid } = useOrg()
  const queryClient = useQueryClient()
  const [saving, setSaving] = useState(false)

  const { data: externalRoles = [] } = useExternalRoles()
  // Search server-side rather than loading a capped list: on a real book this
  // fetched the first 500 of 8,000+ partners by name and silently dropped the
  // rest, so a parent past "Arnold Clark Rutherglen" simply could not be
  // chosen and nothing said so.
  const [parentSearch, setParentSearch] = useState('')
  const debouncedParentSearch = useDebouncedValue(parentSearch, 250)
  const { data: parentPartnersData } = usePartnerships(
    open && debouncedParentSearch.length >= 2
      ? { search: debouncedParentSearch, page_size: '20' }
      : undefined,
  )
  const { data: rosterData, isPending: rosterLoading } =
    usePartnershipProductRoster(open)
  const availableProducts = useMemo(
    () => rosterData?.available_products ?? [],
    [rosterData],
  )

  // The product roster resolves asynchronously, after the dialog is already
  // open. If it fed the field config / useForm deps directly, its arrival would
  // change `depsKey`, recompute `processedFields`, give `Form` a new identity
  // and REMOUNT the whole form - which closes any dropdown the user has open
  // (the role Select). So the picker reads the live roster from refs kept
  // current on every render, and roster load never churns the form. FormBody
  // re-invokes the field's render on each parent re-render, so the ref value is
  // always fresh at paint time.
  const availableProductsRef = useRef(availableProducts)
  availableProductsRef.current = availableProducts
  const rosterLoadingRef = useRef(rosterLoading)
  rosterLoadingRef.current = rosterLoading
  const parentSearchRef = useRef(parentSearch)
  parentSearchRef.current = parentSearch

  // Refetch roles and partnerships each time the dialog opens so
  // newly created roles/partners are available immediately.
  useEffect(() => {
    if (open) {
      queryClient.invalidateQueries({ queryKey: ['external-roles'] })
      queryClient.invalidateQueries({ queryKey: ['partnerships'] })
    }
  }, [open, queryClient])

  const close = () => {
    onClose?.()
    onOpenChange?.(false)
  }
  const parentPartners = useMemo(
    () => parentPartnersData?.results ?? [],
    [parentPartnersData],
  )
  const parentPartnersRef = useRef(parentPartners)
  parentPartnersRef.current = parentPartners

  const fields = useMemo<FormField[]>(() => [
    {
      type: 'text',
      name: 'organisation_name',
      testId: 'partners-name-input',
      label: 'Partner Name',
      placeholder: 'e.g. Arnold Clark Ford Glasgow',
      value: '',
      required: 'Partner name is required',
      className: 'col-span-2',
    },
    {
      type: 'email',
      name: 'admin_email',
      testId: 'partners-email-input',
      label: 'Contact Email',
      placeholder: 'Their main contact email',
      value: '',
      required: 'Enter a valid email',
      className: 'col-span-2',
    },
    {
      type: 'text',
      name: 'admin_first_name',
      testId: 'partners-firstname-input',
      label: 'First Name',
      placeholder: 'First name',
      value: '',
      className: 'col-span-1',
    },
    {
      type: 'text',
      name: 'admin_last_name',
      testId: 'partners-lastname-input',
      label: 'Last Name',
      placeholder: 'Last name',
      value: '',
      className: 'col-span-1',
    },
    {
      type: 'select',
      name: 'role_uuid',
      testId: 'partners-role-input',
      label: 'What can this partner do?',
      placeholder: 'Select a role...',
      value: '',
      required: 'Please select what this partner can do',
      hint: t(
        'partnerships.invitePartnerRoleHint',
        'Sets the partner capability - the workflow they run when a claim is assigned to them (e.g. inspection, assessment, or repair).',
      ),
      options: externalRoles.map((role) => ({ value: role.uuid, label: role.name })),
    },
    {
      type: 'select',
      name: 'user_management_strategy',
      testId: 'partners-strategy-input',
      label: 'Who manages their users?',
      value: 'managed',
      options: STRATEGY_OPTIONS.map((option) => ({ value: option.value, label: option.label })),
    },
    {
      type: 'select',
      name: 'partner_type',
      testId: 'partners-type-input',
      label: 'Partner type',
      value: 'standalone',
      // Labels only. The VALUES are stored and drive conditional fields
      // (partner_type === 'subsidiary' reveals the parent picker), so they stay.
      options: [
        {
          value: 'standalone',
          label: t('partnerships.standalone', 'Standalone'),
        },
        { value: 'parent', label: t('partnerships.parent', 'Head office') },
        {
          value: 'subsidiary',
          label: t('partnerships.subsidiary', 'Location'),
        },
      ],
      className: 'col-span-2',
    },
    {
      // Custom rather than `select`: the option list is server-searched, and a
      // select cannot take a search term. Reads live results from refs so the
      // field config stays stable - putting the results in here would change
      // depsKey, remount the form, and close the list on every keystroke.
      type: 'custom',
      name: 'parent_partnership_uuid',
      value: '',
      visible: (ctx) => ctx.values.partner_type === 'subsidiary',
      className: 'col-span-2',
      render: (formContext) => (
        <ParentPartnerPicker
          selected={(formContext.values.parent_partnership_uuid ?? '') as string}
          onChange={(uuid) =>
            formContext.setFieldValue('parent_partnership_uuid', uuid)
          }
          initialTerm={parentSearchRef.current}
          onSearch={setParentSearch}
          results={parentPartnersRef.current}
        />
      ),
    },
    {
      type: 'checkbox',
      name: 'downstream_mfa_required',
      testId: 'partners-mfa-input',
      label: 'Require two-factor authentication',
      value: false,
      className: 'col-span-2',
    },
    {
      type: 'custom',
      name: 'product_uuids',
      className: 'col-span-2',
      label: t('partnerships.invitePartnerProductsLabel', 'Products'),
      value: [],
      validationSchema: z
        .array(z.string())
        .min(
          1,
          t(
            'partnerships.invitePartnerProductsRequired',
            'Select at least one product for this partner.',
          ),
        ),
      render: (formContext) => {
        const value = (formContext.values.product_uuids ?? []) as string[]
        const error = formContext.errors.product_uuids as string | undefined
        return (
          <InviteProductSelector
            products={availableProductsRef.current}
            loading={rosterLoadingRef.current}
            selected={value}
            onChange={(next) => {
              formContext.setFieldValue('product_uuids', next)
              // setFieldValue does not re-validate, so once the required
              // selection is satisfied clear the highlight the failed submit
              // raised (otherwise the red ring/message would linger).
              if (next.length > 0) {
                formContext.setFieldError('product_uuids', undefined)
              }
            }}
            label={t('partnerships.invitePartnerProductsLabel', 'Products')}
            hint={t(
              'partnerships.invitePartnerProductsHint',
              'Choose the products this partner can access. At least one is required - a partner with no products has nothing to work on.',
            )}
            emptyMessage={t(
              'partnerships.invitePartnerProductsEmpty',
              'No products found. Add a product before inviting a partner.',
            )}
            error={error}
          />
        )
      },
    },
  ], [externalRoles, t])
  const { Form, ref } = useForm(
    {
      name: 'invite-partner-form',
      submit: 'footer',
      fields,
    },
    async (rawValues) => {
      if (!orgUuid) return
      const values = rawValues as Record<string, string | boolean | string[]>
      if (values.partner_type === 'subsidiary' && !values.parent_partnership_uuid) {
        toast.error(
          t(
            'partnerships.invitePartnerParentRequired',
            'Please select the parent partner',
          ),
        )
        return
      }
      // Hard block: a partner must be invited with at least one product (the
      // server also rejects an empty list). The schema `.min(1)` blocks this
      // submit and highlights the products field; this guard is defence in
      // depth so a future refactor that drops the schema still fails loudly.
      const productUuids = Array.isArray(values.product_uuids)
        ? (values.product_uuids as string[])
        : []
      if (productUuids.length === 0) {
        toast.error(
          t(
            'partnerships.invitePartnerProductsRequired',
            'Select at least one product for this partner.',
          ),
        )
        return
      }
      setSaving(true)
      try {
        const payload: Record<string, unknown> = {
          organisation_name: String(values.organisation_name ?? '').trim(),
          admin_email: String(values.admin_email ?? '').trim(),
          admin_first_name: String(values.admin_first_name ?? '').trim(),
          admin_last_name: String(values.admin_last_name ?? '').trim(),
          role_uuid: String(values.role_uuid ?? ''),
          user_management_strategy: String(values.user_management_strategy ?? 'managed'),
          downstream_mfa_required: Boolean(values.downstream_mfa_required),
          product_uuids: productUuids,
        }
        if (values.partner_type === 'subsidiary' && values.parent_partnership_uuid) {
          payload.parent_partnership_uuid = String(values.parent_partnership_uuid)
        }
        const { data: invitation } = await api.post(`/organisations/${orgUuid}/partnership-invitations/`, payload)
        if (invitation?.email_sent === false) {
          toast.warning('Partner added but the invitation email could not be sent. You can resend it from the list.')
        } else {
          toast.success('Partner invitation sent')
        }
        onCreated?.()
        onSuccess?.()
        close()
      } catch (err) {
        toastApiError(err, 'Failed to send invitation');
      } finally {
        setSaving(false)
      }
    },
    [open, fields, externalRoles, orgUuid, t],
  )

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value) {
          ref.current?.resetForm({
            values: {
              organisation_name: '',
              admin_email: '',
              admin_first_name: '',
              admin_last_name: '',
              role_uuid: '',
              user_management_strategy: 'managed',
              partner_type: '',
              parent_partnership_uuid: '',
              downstream_mfa_required: false,
              product_uuids: [],
            },
          })
          close()
        }
      }}
    >
      <DialogContent className="max-w-2xl sm:max-w-2xl">
        <DialogHeader className="mb-1">
          <DialogTitle>Add a Partner</DialogTitle>
          <DialogDescription>
            {t(
              'partnerships.invitePartnerDescription',
              'Invite a downstream organisation to your network. Their role determines what they can do on your claims.',
            )}
          </DialogDescription>
        </DialogHeader>
        <Form className="grid grid-cols-2 gap-x-6 gap-y-6">
          <DialogFooter className="col-span-2 border-t-[0.5px] border-t-[var(--rule)] pt-4 mt-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                ref.current?.resetForm({
                  values: {
                    organisation_name: '',
                    admin_email: '',
                    admin_first_name: '',
                    admin_last_name: '',
                    role_uuid: '',
                    user_management_strategy: 'managed',
                    downstream_mfa_required: false,
                    product_uuids: [],
                  },
                })
                close()
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              data-testid="partners-dialog-submit"
            >
              {saving && <Loader2 className="h-3 w-3 animate-spin mr-1.5" />}
              Send Invitation
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
